import { useSearchParams } from 'react-router-dom';

import { THEME_CONFIG, type ThemeSlug, themeCodeOfSlug } from './model/themes.config';
import type { ThemeCode } from './model/types';

/** The dashboard's selected theme: the Summary, or one theme by its URL slug. */
export type AwpTheme = 'summary' | ThemeSlug;

export interface AwpDashboardParams {
  theme: AwpTheme;
  /** The selected theme's code, for filtering rows; `null` on the Summary. */
  themeCode: ThemeCode | null;
  selectTheme: (theme: AwpTheme) => void;
}

const THEME_PARAM = 'theme';

/**
 * The dashboard's view state, kept in the URL so Back and deep links behave the same in both portals.
 *
 * The URL holds view state only — the theme's slug, never a member id or a result. The Summary is the
 * param's absence, and anything unrecognised reads as the Summary without rewriting the URL. Selecting
 * replaces the history entry, so Back leaves the dashboard instead of stepping through theme clicks, and
 * other params on the page are kept.
 */
export function useAwpDashboardParams(): AwpDashboardParams {
  const [searchParams, setSearchParams] = useSearchParams();
  const themeCode = themeCodeOfSlug(searchParams.get(THEME_PARAM) ?? '') ?? null;

  const selectTheme = (theme: AwpTheme) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (theme === 'summary') next.delete(THEME_PARAM);
        else next.set(THEME_PARAM, theme);
        return next;
      },
      { replace: true },
    );
  };

  return { theme: themeCode ? THEME_CONFIG[themeCode].slug : 'summary', themeCode, selectTheme };
}
